import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, weekImageUrl } from "@/lib/apiClient";
import { MessageCircle, ArrowUpRight } from "lucide-react";

function WeekCard({ w }) {
  return (
    <Link
      to={`/archive/${w.id}`}
      data-testid={`week-card-${w.week_number}`}
      className="group block bg-surface border border-archive-border offset-shadow hover:-translate-y-1 transition-transform duration-300"
    >
      <div className="aspect-[3/4] overflow-hidden bg-highlight border-b border-archive-border relative">
        <img
          src={weekImageUrl(w.id)}
          alt={w.title}
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-0 left-0 bg-ink/85 text-parchment font-mono text-[10px] tracking-widest uppercase px-3 py-1.5">
          Week {String(w.week_number).padStart(2, "0")}
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-serif text-2xl text-ink leading-tight tracking-tight">{w.title}</h3>
          <ArrowUpRight
            strokeWidth={1.5}
            className="w-5 h-5 text-text-muted shrink-0 group-hover:text-oxblood transition-colors"
          />
        </div>
        {w.page_label && (
          <div className="font-mono text-[10px] tracking-widest uppercase text-oxblood mt-2">
            {w.page_label}
          </div>
        )}
        {w.description && (
          <p className="font-body text-sm text-text-muted mt-3 leading-relaxed line-clamp-2">
            {w.description}
          </p>
        )}
        <div className="flex items-center gap-2 mt-5 font-mono text-xs text-text-muted">
          <MessageCircle strokeWidth={1.5} className="w-3.5 h-3.5" />
          {w.comment_count} {w.comment_count === 1 ? "remark" : "remarks"}
        </div>
      </div>
    </Link>
  );
}

export default function Archive() {
  const [weeks, setWeeks] = useState(null);

  useEffect(() => {
    api.get("/weeks").then(({ data }) => setWeeks(data)).catch(() => setWeeks([]));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="max-w-2xl mb-14 animate-fade-up">
        <div className="font-mono text-xs tracking-[0.3em] uppercase text-oxblood mb-4">
          The Archive
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-none">
          Weekly Pages
        </h1>
        <p className="font-body text-text-muted mt-6 leading-relaxed">
          Every unsealed leaf of the 1872 volume. Select a week to read the page and join its
          discussion.
        </p>
      </div>

      {weeks === null ? (
        <div className="font-mono text-xs tracking-widest uppercase text-text-muted animate-pulse py-20">
          Retrieving pages…
        </div>
      ) : weeks.length === 0 ? (
        <div
          data-testid="archive-empty"
          className="border border-dashed border-archive-border bg-surface p-16 text-center"
        >
          <p className="font-serif text-3xl text-ink mb-3">No pages unsealed yet</p>
          <p className="font-body text-text-muted">
            The archivist has not yet shared the first weekly page. Check back soon.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {weeks.map((w) => (
            <WeekCard key={w.id} w={w} />
          ))}
        </div>
      )}
    </div>
  );
}
